import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Coffee, Trash2, Clock, Volume2, VolumeX, Copy, Edit2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useBreakReminder } from '../../contexts/BreakReminderContext';
import CreateBreakModal from './CreateBreakModal';
import DeleteModal from '../Common/DeleteModal';
import ContextMenu from '../Common/ContextMenu';

const BreakReminderSidebar = () => {
  const { t } = useTranslation('breaks');
  const daysOfWeek = t('daysShort', { returnObjects: true });
  const { breaks, selectedBreak, createBreak, deleteBreak, selectBreak, duplicateBreak } = useBreakReminder();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [breakSearchQuery, setBreakSearchQuery] = useState('');
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, breakId: null });
  const [contextMenuState, setContextMenuState] = useState({
    isOpen: false,
    position: { x: 0, y: 0 },
    breakItem: null,
  });

  const handleCreateBreak = (breakData) => {
    const newBreak = createBreak(breakData);
    selectBreak(newBreak);
  };

  const handleDeleteClick = (breakId, e) => {
    e.stopPropagation();
    setDeleteModal({ isOpen: true, breakId });
  };

  const openDeleteModal = (breakId) => {
    setDeleteModal({ isOpen: true, breakId });
  };

  const handleDeleteConfirm = () => {
    if (deleteModal.breakId) {
      deleteBreak(deleteModal.breakId);
      if (selectedBreak?.id === deleteModal.breakId) {
        selectBreak(null);
      }
    }
    setDeleteModal({ isOpen: false, breakId: null });
  };

  const getActiveDaysText = (activeDays) => {
    const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const activeDayIndices = dayKeys
      .map((key, index) => (activeDays[key] ? index : -1))
      .filter((index) => index !== -1);
    
    if (activeDayIndices.length === 7) return t('activeDaysAll');
    if (activeDayIndices.length === 5 && activeDayIndices.every(i => i < 5)) return t('activeDaysWeekdays');
    if (activeDayIndices.length === 0) return t('activeDaysNone');
    
    return activeDayIndices.map(i => daysOfWeek[i]).join(', ');
  };

  const handleContextMenu = (event, breakItem) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenuState({
      isOpen: true,
      position: { x: event.clientX, y: event.clientY },
      breakItem,
    });
  };

  const closeContextMenu = () => {
    setContextMenuState((prev) => ({ ...prev, isOpen: false, breakItem: null }));
  };

  const handleDuplicateBreak = async (breakItem) => {
    const duplicated = await duplicateBreak(breakItem.id);
    if (duplicated) {
      selectBreak(duplicated);
    }
  };

  const contextMenuItems = contextMenuState.breakItem
    ? [
        {
          label: t('sidebar.editLabel'),
          icon: Edit2,
          onClick: () => {
            const targetBreak = contextMenuState.breakItem;
            if (!targetBreak) return;
            selectBreak(targetBreak);
          },
        },
        {
          label: t('sidebar.duplicateLabel'),
          icon: Copy,
          onClick: () => {
            const targetBreak = contextMenuState.breakItem;
            if (!targetBreak) return;
            handleDuplicateBreak(targetBreak);
          },
        },
        {
          label: t('sidebar.deleteLabel'),
          icon: Trash2,
          onClick: () => {
            const targetBreak = contextMenuState.breakItem;
            if (!targetBreak) return;
            openDeleteModal(targetBreak.id);
          },
          danger: true,
        },
      ]
    : [];

  const filteredBreaks = (breakSearchQuery || '').trim()
    ? breaks.filter((b) => (b.name || '').toLowerCase().includes((breakSearchQuery || '').trim().toLowerCase()))
    : breaks;

  return (
    <div className="flex flex-col h-full overflow-hidden bg-transparent relative">
      {/* Search Header - h-16 to align with main Header */}
      <div className="h-16 flex items-center px-4 border-b border-slate-200 dark:border-white/5 shrink-0">
        <div className="relative w-full">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 material-symbols-outlined" style={{ fontSize: '20px' }}>search</span>
          <input
            type="text"
            placeholder={t('sidebar.searchPlaceholder')}
            value={breakSearchQuery}
            onChange={(e) => setBreakSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-slate-100 dark:bg-[var(--color-bg-base)] border-none rounded-lg text-sm text-slate-900 dark:text-slate-200 placeholder-slate-500 dark:placeholder-slate-500 focus:ring-2 focus:ring-primary/50"
          />
        </div>
      </div>

      {/* Breaks List */}
      <div className="flex-1 overflow-y-auto p-3 min-h-0 relative pb-24">
        <AnimatePresence>
          {breaks.length === 0 ? (
            <div className="text-center text-gray-500 dark:text-[var(--color-text-secondary)] text-sm py-12">
              <div className="flex flex-col items-center space-y-2">
                <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-[var(--color-bg-elevated)] flex items-center justify-center border border-gray-200 dark:border-white/10">
                  <Coffee className="w-6 h-6 text-gray-400 dark:text-[var(--color-text-secondary)]" />
                </div>
                <p>{t('sidebar.emptyTitle')}</p>
                <p className="text-xs text-gray-500 dark:text-[var(--color-text-secondary)]">{t('sidebar.emptyHint')}</p>
              </div>
            </div>
          ) : filteredBreaks.length === 0 ? (
            <div className="text-center text-gray-500 dark:text-[var(--color-text-secondary)] text-sm py-8">
              <p>{t('sidebar.noMatchTitle')}</p>
              <p className="text-xs mt-1">{t('sidebar.noMatchHint')}</p>
            </div>
          ) : (
            filteredBreaks.map((breakItem) => (
              <motion.div
                key={breakItem.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
                className={`mb-2.5 p-3.5 rounded-md cursor-pointer transition-all duration-200 group ${
                  selectedBreak?.id === breakItem.id
                    ? 'bg-primary/20 border-primary shadow-elevated'
                    : 'bg-white dark:bg-[var(--color-bg-muted)] hover:bg-gray-50 dark:hover:bg-[var(--color-bg-base)] border border-gray-200 dark:border-white/5 hover:border-gray-300 dark:hover:border-white/10'
                }`}
                onClick={() => selectBreak(breakItem)}
                onContextMenu={(event) => handleContextMenu(event, breakItem)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Coffee className="w-4 h-4 text-[#3d8ef0] dark:text-[#7ab8f8] flex-shrink-0" strokeWidth={2} />
                      <h3 className="font-semibold text-gray-900 dark:text-[#e8f0fb] truncate text-sm leading-tight">
                        {breakItem.name}
                      </h3>
                    </div>
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                        <Clock className="w-3.5 h-3.5" strokeWidth={2} />
                        <span>{breakItem.startTime}</span>
                        <span className="text-gray-400 dark:text-gray-400">•</span>
                        <span>{breakItem.durationMinutes} {t('sidebar.minutesShort')}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                        <span className="truncate">{getActiveDaysText(breakItem.activeDays)}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-500">
                        {breakItem.soundEnabled ? (
                          <>
                            <Volume2 className="w-3 h-3" strokeWidth={2} />
                            <span>{t('sidebar.soundOn')}</span>
                          </>
                        ) : (
                          <>
                            <VolumeX className="w-3 h-3" strokeWidth={2} />
                            <span>{t('sidebar.soundOff')}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex-shrink-0">
                    <motion.button
                      whileHover={{ scale: 1.1, backgroundColor: 'rgba(239, 68, 68, 0.2)' }}
                      whileTap={{ scale: 0.9 }}
                      onClick={(e) => handleDeleteClick(breakItem.id, e)}
                      className="p-1.5 rounded-lg hover:bg-red-500/20 text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-all duration-200"
                      title={t('sidebar.deleteTooltip')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </div>

      {/* Floating Action Button (FAB) - same position as Notes */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setShowCreateModal(true)}
        className="absolute bottom-6 right-6 w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-500/30 flex items-center justify-center text-white cursor-pointer z-50 transition-transform hover:scale-105"
        title={t('sidebar.fabTitle')}
      >
        <Plus className="w-6 h-6" strokeWidth={2.5} />
      </motion.button>

      {/* Create Modal */}
      <CreateBreakModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreateBreak}
      />

      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, breakId: null })}
        onConfirm={handleDeleteConfirm}
        title={t('sidebar.deleteModalTitle')}
        message={t('sidebar.deleteModalMessage')}
      />
      <ContextMenu
        isOpen={contextMenuState.isOpen}
        position={contextMenuState.position}
        items={contextMenuItems}
        onClose={closeContextMenu}
      />
    </div>
  );
};

export default BreakReminderSidebar;

